import React from 'react';
import { useDispatch } from 'react-redux';
import { change, reset, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { useApplyWizardContext } from '../../context/ApplyWizardContext';
import { RootState } from '@store/reducers';
import SignUpLandingShell, { SignUpLandingHowItWorksStep } from './_shared/SignUpLandingShell';
import { flowStepComplete } from '@utils/gaFlow';

const HERO_IMAGE_SRC = 'https://media.tento.co/photos/apply-landing-hero.jpg';
const HIW_IMAGE_SRC = 'https://media.tento.co/photos/apply-landing-hiw.jpg';

const HIW_STEPS: SignUpLandingHowItWorksStep[] = [
  {
    number: '01',
    title: 'Submit your application',
    body: 'Fill out our seamless application in less than 5 minutes.',
  },
  {
    number: '02',
    title: 'Pre–qualify in minutes',
    body: 'Find out immediately if you pre–qualify, then simply submit your bank statements for review.',
  },
  {
    number: '03',
    title: 'Get funded in hours',
    body: 'Secure your funding in hours, not days, and get the equipment to keep your business running.',
  },
];

type ConnectedProps = {
  user: RootState['user'];
  loanId?: string;
  prefilledValues: Record<string, unknown>;
};

const ApplyLandingPageConnected: React.FC<ConnectedProps> = ({ user, loanId, prefilledValues }) => {
  const dispatch = useDispatch();
  const wizardContext = useApplyWizardContext();

  const handleApply = () => {
    flowStepComplete('apply_financing', 'landing');
    dispatch(change('application', 'application_type', 'apply'));
    if (user.loggedIn && loanId) {
      // Continue existing application — keep current state.
    } else {
      dispatch(reset('application'));
      dispatch(change('application', 'loan_amount', 150000));
      dispatch(change('application', 'loan_terms', 60));
      dispatch(change('application', 'owner_1_email', user.loggedIn ? user.email : ''));
      dispatch(change('application', 'application_type', 'apply'));
      if (prefilledValues && Object.keys(prefilledValues).length > 0) {
        Object.keys(prefilledValues).forEach((key) => {
          dispatch(change('application', key, (prefilledValues as Record<string, unknown>)[key]));
        });
      }
      wizardContext.resetFarthestStep?.();
    }
    wizardContext.changeStepToFirst?.();
  };

  return (
    <SignUpLandingShell
      heroImage={HERO_IMAGE_SRC}
      heroAlt="Small business owner reviewing financing on a tablet"
      heroLabel="GET FINANCED TODAY"
      heroHeadline="Equipment financing and working capital made for small business"
      heroParagraph="Fast, flexible financing bringing you the funds that your business needs to achieve your next milestone."
      hiwImage={HIW_IMAGE_SRC}
      hiwAlt="Small business operator working with equipment"
      hiwHeadline="How financing with us actually works"
      hiwSteps={HIW_STEPS}
      bottomCtaHeadline="Join the thousands of small businesses who have trusted Tento for financing."
      primaryCtaLabel="Apply Now"
      onPrimaryCta={handleApply}
    />
  );
};

const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => ({
  user: state.user,
  loanId: appSelector(state, 'loan_id') as string | undefined,
  prefilledValues: state.form.prefilled?.values ?? {},
});

export const ApplyLandingPage = connect(mapStateToProps)(ApplyLandingPageConnected);

export default ApplyLandingPage;
